import { Platform, StyleSheet } from 'react-native';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StackActions } from '@react-navigation/native';
import { Controller, FormProvider } from 'react-hook-form';

import {
	colors,
	spacing,
	Button,
	Text,
	Link,
	FormInput,
	UsernameFormInput,
	CheckBox,
} from '@pletly/ui';
import { PrivacyPolicyLink } from '@pletly/feature-about';
import { PasswordRequirementsHint } from '../../components/PasswordRequirementsHint';
import { AuthContainer } from '../../components/AuthContainer/AuthContainer';
import { Email } from '../../components/Email';
import { Password } from '../../components/Password';
import { withTranslation } from '../../hoc/withTranslation';
import { useRegister } from './components/useRegister';
import { RootStackScreenProps } from '../../app/UnauthenticatedApp/types';

function Register({ route: { params } }: RootStackScreenProps<'RegisterWithLink' | 'Register'>) {
	const { t } = useTranslation();
	const { formMethods, isSending, onSubmit } = useRegister();

	const registerCode = params?.code;

	useEffect(() => {
		if (!registerCode) {
			return;
		}

		formMethods.setValue('registerCode', registerCode);
	}, [formMethods, registerCode]);

	return (
		<AuthContainer
			title={t('register.hello-friend')}
			subtitle={t('register.provide-your-personal-details')}
		>
			<FormProvider {...formMethods}>
				<Text category="h1" status="primary">
					<>{t('register.create-account')}</>
				</Text>
				<Text
					category="s1"
					status="primary"
					style={styles.privacyPolicy}
					align={Platform.select({ web: 'left', default: 'center' })}
				>
					{t('register.agree-to')}{' '}
					<PrivacyPolicyLink category="s2" status="info" style={styles.privacyPolicyLink}>
						{t('register.privacy-policy-and-eula')}
					</PrivacyPolicyLink>
					.
				</Text>
				<FormInput
					name="email"
					InputComponent={Email}
					{...Platform.select({
						web: {
							label: t('common.e-mail'),
							placeholder: t('common.enter-your-email'),
							accessoryLeft: undefined,
						},
					})}
					autoFocus
					defaultValue={params?.email}
				/>
				<UsernameFormInput
					label={t('common.username')}
					placeholder={t('common.enter-your-username')}
					accessoryLeft={undefined}
					gutter
					showGenerator
				/>
				<FormInput
					testID="password"
					name="password"
					textContentType="newPassword"
					InputComponent={Password}
					{...Platform.select({
						web: {
							label: t('common.password'),
							placeholder: t('common.create-a-password'),
							accessoryLeft: undefined,
						},
					})}
					gutter
					onSubmitEditing={onSubmit}
				/>
				<PasswordRequirementsHint value={formMethods.watch('password')} />

				<Controller
					name="newsletter"
					control={formMethods.control}
					render={({ field: { onChange, value } }) => (
						<CheckBox style={styles.newsletter} checked={value} onChange={onChange}>
							{t('register.sign-up-for-a-newsletter')}
						</CheckBox>
					)}
				/>
				<Button
					testID="register-button"
					loading={isSending}
					color="primary"
					variant="filled"
					onPress={onSubmit}
					style={styles.signButton}
				>
					{t('login.sign-up')}
				</Button>

				<Text
					style={styles.toSignIn}
					align="center"
					category={Platform.select({ web: 's1', default: undefined })}
				>
					{`${t('register.do-you-have-account')} `}
					<Link screen="Login" style={styles.toSignInLink} action={StackActions.replace('Login')}>
						<Text category={Platform.select({ web: 's2', default: undefined })}>
							{t('login.sign-in')}
						</Text>
					</Link>
				</Text>
			</FormProvider>
		</AuthContainer>
	);
}

const styles = StyleSheet.create({
	toSignIn: {
		color: colors.primarySemiTransparent,
		fontFamily: 'Poppins-Medium',
		marginTop: spacing(3),
	},
	toSignInLink: {
		color: colors.primary,
		marginLeft: spacing(),
	},
	signButton: {
		marginTop: spacing(3),
	},
	privacyPolicy: {
		paddingTop: 8,
		paddingBottom: 20,
	},
	privacyPolicyLink: {
		textDecorationLine: 'underline',
		color: colors.primary700,
	},
	newsletter: {
		marginTop: spacing(3),
	},
});

export default withTranslation(Register);
